import { useState, useEffect } from 'react';
import { NumericFormat } from 'react-number-format';
import { HomeData, InputType, inputDefault } from '../utils/data';
import { DisplaySymbol } from '../utils/currency';
import {
  ROUNDUP,
  formatNumberWithAbbreviations,
  formatNumberWithAbbreviationsLetters,
} from '../utils/Formatters';
import TCOBarChart from './charts/tcoChart';
import './style.scss';
import 'rc-slider/assets/index.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap/dist/js/bootstrap.js';
import * as URX_FORM from '../constant/urx-form';
import { useEnv } from 'src/common/hooks/widgets/useEnv';
import Cookies from 'js-cookie';
import { Helmet } from 'react-helmet';
import { FormattedMessage, useIntl } from 'react-intl';
import messages from '../locales/messages';
import { FusionHCIData } from '../utils/FusionHCI';
import {
  MaxServersAdmin,
  nbr_VMs,
  NbrAdmin,
  Salary,
  StorageTypes,
} from '../utils/defaults';
import { PDFDownloadLink, BlobProvider } from '@react-pdf/renderer';
import { PDFGenerate } from './PDFGenerate';
import {
  Button,
  Slider,
  Tooltip,
  Checkbox,
  Toggle,
  Select,
  SelectItem,
  Accordion,
  AccordionItem,
  Modal,
} from 'carbon-components-react';
import { FormModal } from 'src/common/utils/FormModal';
import {
  urxScript,
  urxScriptLoader,
} from 'src/common/hooks/widgets/globalDefinitions';
import { initUrxForm } from 'src/common/hooks/widgets/urxForm';
import { IBMLocale } from 'src/common/locale/mapValidLocale';
import intelImage from '../assets/images/IntelLogo.png';
import ibmImage from '../assets/images/ibmLogo.png';
import '../../../common/style/CarbonV2Fixes.scss';

const ESGIBMFusion = () => {
  const [inputType, setInputType] = useState<InputType>(inputDefault);
  const [isAdvanceSelected, setIsAdvanceSelected] = useState(true);

  const [showAssumption, setShowAssumption] = useState(false);
  const [isAssumptionDefaultValue, setIsAssumptionDefaultValue] =
    useState(false);
  const [environment] = useEnv();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const intl = useIntl();
  const [chartDataUrl, setChartDataUrl] = useState('');
  const [totalWorkloadPercentage, setTotalWorkloadPercentage] = useState(0);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [triggerDownload, setTriggerDownload] = useState(false);

  //#region handlers
  const initialState = {
    error: false,
    isLoaded: false,
    max: 100,
    showTooltip: false,
    homeData: new HomeData(),

    fusionHCIData: new FusionHCIData(),
  };

  const [state, setState] = useState(initialState);

  const props = {
    totalCurrentCost: state.fusionHCIData.totalCurrentCost,
    totalIBMCost: state.fusionHCIData.totalIBMCost,
    totalCost: state.fusionHCIData.totalCost,
    inputType: inputType,
    isAdvanceSelected: isAdvanceSelected,
    chartDataUrl: chartDataUrl,
  };
  const pdfObject = PDFGenerate(props);

  window.urxEnvironment = environment;
  window.onUrxFormSubmitSuccess = () => {
    setDrawerOpen(false);
    setInputType({ ...inputType, openResult: true });
    setShowDownloadModal(true);
    setTriggerDownload(true);
  };

  const handleInputChange = (
    newValue: number | number[],
    inputName: string,
  ) => {
    // Ensure newValue is a single number
    const value = Array.isArray(newValue) ? newValue[0] : newValue; // Take first value if it's an array
    // 1. Get the current values of all sliders
    const currentValues = {
      xSmallSize: inputType.xSmallSize,
      smallSize: inputType.smallSize,
      mediumSize: inputType.mediumSize,
      largeSize: inputType.largeSize,
      xLargeSize: inputType.xLargeSize,
    };

    // 2. Calculate the total sum of the current values (excluding the one being changed)
    const currentTotal = Object.values(currentValues)
      .filter((val, i) => Object.keys(currentValues)[i] !== inputName)
      .reduce((sum, val) => sum + val, 0);

    // 3. Calculate the remaining available value
    const remainingValue = 50000 - currentTotal;

    // 4. If the new value exceeds the remaining value, adjust it
    const adjustedValue = Math.min(value, remainingValue);

    // 5. Update the state with the adjusted value
    setInputType({ ...inputType, [inputName]: adjustedValue });
  };

  const handleVMWorkloadFunctions = (
    value: number | number[],
    slider: string,
  ): void => {
    if (typeof value === 'number') {
      const [val] = state.homeData.VMWorkloadValuesSum(value, slider);
      setInputType({ ...inputType, [slider]: val });
    }
  };

  const resetDefault = () => {
    setInputType({
      ...inputType,
      maxServersAdmin: MaxServersAdmin.default,
      NbrAdmin: NbrAdmin.default,
      salary: Salary.default,
    });
  };

  const assumptionDefault = () => {
    if (
      inputType.maxServersAdmin == MaxServersAdmin.default &&
      inputType.NbrAdmin == NbrAdmin.default &&
      inputType.salary == Salary.default
    ) {
      setIsAssumptionDefaultValue(true);
    } else {
      setIsAssumptionDefaultValue(false);
    }
  };

  const setCookie = () => {
    Cookies.set(
      'urxdcq',
      JSON.stringify({
        source: URX_FORM.FORM_ID,
        // One field to provide all values (stringify)
        dcq: {
          Q_DYNAMIC_Q_1: inputType.nbr_VMs.toString(),

          Q_DYNAMIC_Q_2: inputType.xSmallSize.toString(),
          Q_DYNAMIC_Q_3: inputType.smallSize.toString(),
          Q_DYNAMIC_Q_4: inputType.mediumSize.toString(),
          Q_DYNAMIC_Q_5: inputType.largeSize.toString(),
          Q_DYNAMIC_Q_6: inputType.xLargeSize.toString(),
          Q_DYNAMIC_Q_7: inputType.totalVMs.toString(),

          Q_DYNAMIC_Q_8: inputType.replatform.toString(),
          Q_DYNAMIC_Q_9: inputType.storageType.toString(),

          Q_DYNAMIC_Q_10: inputType.vmWorkloadDatabase.toString(),
          Q_DYNAMIC_Q_11: inputType.vmWorkloadAIML.toString(),
          Q_DYNAMIC_Q_12: inputType.vmWorkloadDataAnalytics.toString(),
          Q_DYNAMIC_Q_13: inputType.vmWorkloadMessaging.toString(),
          Q_DYNAMIC_Q_14: inputType.vmWorkloadWebServers.toString(),

          Q_DYNAMIC_Q_15: inputType.containerizeDatabases.toString(),
          Q_DYNAMIC_Q_16: inputType.containerizeAIML.toString(),
          Q_DYNAMIC_Q_17: inputType.containerizeDataAnalytics.toString(),
          Q_DYNAMIC_Q_18: inputType.containerizeMessaging.toString(),
          Q_DYNAMIC_Q_19: inputType.containerizeWebServers.toString(),

          Q_DYNAMIC_Q_20: inputType.snapshots.toString(),
          Q_DYNAMIC_Q_21: inputType.crashConsistent.toString(),
          Q_DYNAMIC_Q_22: inputType.applicationConsistent.toString(),
          Q_DYNAMIC_Q_23: inputType.metroDisaster.toString(),
          Q_DYNAMIC_Q_24: inputType.regionalDisaster.toString(),
        },
      }),
    );
  };

  const handleSliders = (
    value: number,
    controlId: string,
    max: number,
    min: number,
  ) => {
    let result = min;
    if (value >= 1 && value <= max) {
      result = value ?? 0;
    } else if (value >= max) {
      result = max;
    }
    setInputType({
      ...inputType,
      [controlId]: result,
    });
  };

  const withValueCap = (inputObj: any, maxValue: number) => {
    const { value } = inputObj;
    if (value <= maxValue) return true;
    return false;
  };

  const withValueCapFiftyTh = (inputObj: any) => {
    return withValueCap(inputObj, 50000);
  };

  const withValueCapHundred = (inputObj: any) => {
    return withValueCap(inputObj, 100);
  };

  const withValueCapForVMWorkLoad = (inputObj: any) => {
    return withValueCap(inputObj, 101 - totalWorkloadPercentage);
  };
  //#endregion

  //#region useEffects
  useEffect(() => {
    const fusionHCIData = state.fusionHCIData;
    fusionHCIData.Calculate({ input: inputType, setInputType });

    setState({
      ...state,
      fusionHCIData: state.fusionHCIData,
    });
  }, []);

  useEffect(() => {
    const fusionHCIData = state.fusionHCIData;
    fusionHCIData.Calculate({ input: inputType, setInputType });

    setState({
      ...state,
      fusionHCIData: state.fusionHCIData,
    });

    assumptionDefault();
  }, [
    inputType.xSmallSize,
    inputType.smallSize,
    inputType.mediumSize,
    inputType.largeSize,
    inputType.xLargeSize,
    inputType.totalVMs,
    inputType.replatform,
    inputType.maxServersAdmin,
    inputType.salary,
    inputType.currency,
  ]);

  useEffect(() => {
    setInputType({
      ...inputType,
      xSmallSize: (inputType.nbr_VMs * 5) / 100,
      smallSize: (inputType.nbr_VMs * 20) / 100,
      mediumSize: (inputType.nbr_VMs * 50) / 100,
      largeSize: (inputType.nbr_VMs * 20) / 100,
      xLargeSize: (inputType.nbr_VMs * 5) / 100,
      totalVMs:
        (inputType.nbr_VMs * 5) / 100 +
        (inputType.nbr_VMs * 20) / 100 +
        (inputType.nbr_VMs * 50) / 100 +
        (inputType.nbr_VMs * 20) / 100 +
        (inputType.nbr_VMs * 5) / 100,
    });
  }, [inputType.nbr_VMs]);

  useEffect(() => {
    setInputType({
      ...inputType,
      totalVMs:
        inputType.xSmallSize +
        inputType.smallSize +
        inputType.mediumSize +
        inputType.largeSize +
        inputType.xLargeSize,
    });
  }, [
    inputType.xSmallSize,
    inputType.smallSize,
    inputType.mediumSize,
    inputType.largeSize,
    inputType.xLargeSize,
  ]);

  useEffect(() => {
    const fusionHCIData = state.fusionHCIData;
    fusionHCIData.Calculate({
      input: inputType,
      isNbrAdminChanged: true,
      setInputType,
    });

    setState({
      ...state,
      fusionHCIData: state.fusionHCIData,
    });

    assumptionDefault();
  }, [inputType.NbrAdmin]);

  useEffect(() => {
    const total =
      (inputType.vmWorkloadDatabase || 0) +
      (inputType.vmWorkloadAIML || 0) +
      (inputType.vmWorkloadDataAnalytics || 0) +
      (inputType.vmWorkloadMessaging || 0) +
      (inputType.vmWorkloadWebServers || 0);
    setTotalWorkloadPercentage(total);
  }, [
    inputType.vmWorkloadDatabase,
    inputType.vmWorkloadAIML,
    inputType.vmWorkloadDataAnalytics,
    inputType.vmWorkloadMessaging,
    inputType.vmWorkloadWebServers,
  ]);

  //#endregion

  return (
    <div id="esg-ibm-fusion" className="widget-styles">
      <Helmet>
        <script src={urxScript}></script>
        <script src={urxScriptLoader}></script>
      </Helmet>
      <Modal
        open={showAssumption}
        size={'md'}
        onRequestClose={() => setShowAssumption(false)}
        className="result-form"
        passiveModal={true}
        preventCloseOnClickOutside={true}
      >
        <div id="assumptionModal">
          <header className="title assumption">
            <h2>
              <FormattedMessage {...messages.view_modify_assumption} />
            </h2>
          </header>
          <div className="form-box">
            <div className="grey-box">
              <div className="question-row">
                <div className="question-box">
                  <p>
                    <FormattedMessage {...messages.max_servers_per_admin} />
                  </p>
                  <div className="input-field">
                    <NumericFormat
                      required
                      name="maxServersAdmin"
                      type="text"
                      value={inputType?.maxServersAdmin}
                      decimalScale={0}
                      thousandSeparator={true}
                      onValueChange={(e) => {
                        setInputType({
                          ...inputType,
                          maxServersAdmin: e.floatValue ?? 0,
                        });
                      }}
                    />
                    <div className="input-info">
                      <Tooltip id="ttmaxServersAdmin">
                        <p>
                          <FormattedMessage
                            {...messages.max_servers_per_admin_tooltip}
                          />
                        </p>
                      </Tooltip>
                    </div>
                  </div>
                </div>
              </div>

              <div className="question-row">
                <div className="question-box">
                  <p>
                    <FormattedMessage
                      {...messages.number_of_infrastructure_admins}
                    />
                  </p>
                  <div className="input-field">
                    <NumericFormat
                      required
                      name="NbrAdmin"
                      type="text"
                      value={inputType?.NbrAdmin}
                      decimalScale={0}
                      thousandSeparator={true}
                      onValueChange={(e) => {
                        setInputType({
                          ...inputType,
                          NbrAdmin: e.floatValue ?? 0,
                        });
                      }}
                    />
                    <div className="input-info">
                      <Tooltip id="ttNbrAdmin">
                        <p>
                          <FormattedMessage
                            {...messages.number_of_infrastructure_admins_tooltip}
                          />
                        </p>
                      </Tooltip>
                    </div>
                  </div>
                </div>
              </div>
              <div className="question-row">
                <div className="question-box">
                  <p>
                    <FormattedMessage
                      {...messages.infrastructure_admin_annual_salary}
                    />
                  </p>
                  <div className="input-field">
                    <NumericFormat
                      required
                      name="salary"
                      type="text"
                      prefix={DisplaySymbol(inputType?.currency)}
                      value={inputType?.salary}
                      decimalScale={0}
                      thousandSeparator={true}
                      onValueChange={(e) => {
                        setInputType({
                          ...inputType,
                          salary: e.floatValue ?? 0,
                        });
                      }}
                    />
                    <div className="input-info">
                      <Tooltip id="ttsalary">
                        <span>
                          <FormattedMessage
                            {...messages.infrastructure_admin_annual_salary_tooltip1}
                          />{' '}
                          <a
                            href="https://www.salary.com/research/salary/posting/server-administrator-salary"
                            target="_blank"
                            rel="noreferrer"
                          >
                            {
                              <FormattedMessage
                                {...messages.infrastructure_admin_annual_salary_tooltip2}
                              />
                            }
                          </a>
                        </span>
                      </Tooltip>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="graph-btn desktop">
              <Button
                onClick={() => {
                  setShowAssumption(false);
                }}
              >
                {intl.formatMessage(messages.accept_assumption)}
              </Button>
              <Button
                className={`greybtn ${
                  isAssumptionDefaultValue ? 'disable' : ''
                }`}
                onClick={() => {
                  resetDefault();
                }}
                disabled={isAssumptionDefaultValue}
              >
                Reset Defaults
              </Button>
            </div>
          </div>
        </div>
      </Modal>
      <Modal
        open={showDownloadModal}
        onRequestClose={() => setShowDownloadModal(false)}
        passiveModal
        closeButtonLabel="Close"
        className="download-modal"
      >
        <p>
          <FormattedMessage {...messages.download_report} />
        </p>
        <div className="download-modal-buttons">
          <PDFDownloadLink
            document={pdfObject}
            fileName={'FusionReport.pdf'}
            onClick={() => setShowDownloadModal(false)}
          >
            {({ blob, url, loading, error }) =>
              loading
                ? intl.formatMessage(messages.download_report_link_loading)
                : intl.formatMessage(messages.download_report_link)
            }
          </PDFDownloadLink>
        </div>
      </Modal>
      <FormModal
        pageTitle={intl.formatMessage(messages.esgibm_fusion)}
        object={intl.formatMessage(messages.esgibm_fusion)}
        productTitle={intl.formatMessage(messages.esgibm_fusion)}
        pageUrl={''}
        location={'IBMFusion'}
        bpName={''}
        logo={''}
        drawerOpen={drawerOpen}
        setDrawerOpen={setDrawerOpen}
        formId={URX_FORM.FORM_ID}
      ></FormModal>
      <div className="esg-ibm-fusion-main">
        <div className="image-container">
          <img src={ibmImage} alt="IBM Logo" />
          <img src={intelImage} alt="Intel Logo" />
        </div>
        <div className="homepage-content container">
          <div id="ESG-IBM-form" className="homepage-form">
            <div className="title">
              <h2 className="">
                {' '}
                <FormattedMessage {...messages.page_title1} />{' '}
                <span className="blue">
                  <a
                    href="https://www.ibm.com/products/storage-fusion"
                    target="_blank"
                    rel="noreferrer"
                  >
                    <FormattedMessage {...messages.page_title2} />
                  </a>
                </span>
              </h2>
            </div>
            <div className="form-box">
              <div className="grey-box main-box">
                <Toggle
                  size="md"
                  labelText={intl.formatMessage(messages.advanced)}
                  labelA={'no'}
                  labelB={'yes'}
                  id="advanceSelected"
                  toggled={!isAdvanceSelected}
                  onChange={() => {
                    setIsAdvanceSelected(!isAdvanceSelected);
                    if (!isAdvanceSelected) {
                      setInputType({
                        ...inputType,
                        nbr_VMs: ROUNDUP(inputType.totalVMs),
                      });
                    }
                  }}
                />
                {isAdvanceSelected && (
                  <div className="slider-box" id="question-bg-grey">
                    <div className="question-row">
                      <div className="question-box first-box reverse">
                        <p className="info-vms">
                          <FormattedMessage {...messages.number_of_Vms} />
                        </p>
                        <p className="info-title">
                          *
                          <FormattedMessage {...messages.number_of_Vms_note1} />
                          &nbsp;
                          <a
                            className="a-info-title"
                            href="https://www.ibm.com/products/storage-fusion?utm_content=SRCWW&p1=Search&p4=43700076382506025&p5=e&p9=58700007039170147&gclid=Cj0KCQjw4MSzBhC8ARIsAPFOuyU1AA_29Lu7ffgM-dvkIkDhkgB89a123u6VR6196-B5gOaXi8ypZq0aAq0FEALw_wcB&gclsrc=aw.ds"
                            target="_blank"
                            rel="noreferrer"
                          >
                            <FormattedMessage
                              {...messages.number_of_Vms_note_link}
                            />
                          </a>
                          &nbsp;
                          <FormattedMessage {...messages.number_of_Vms_note2} />
                        </p>
                      </div>
                    </div>
                    <div className="question-row">
                      <div className="range-slider ranger-slider-top with-four-labels">
                        <div className="first-slide">
                          <Slider
                            id="nbr_VMs"
                            min={nbr_VMs.min}
                            max={nbr_VMs.max}
                            hideTextInput={true}
                            value={inputType?.nbr_VMs}
                            labelText={null}
                            onChange={({ value }: any) => {
                              if (typeof value === 'number') {
                                handleSliders(
                                  value,
                                  'nbr_VMs',
                                  nbr_VMs.max,
                                  nbr_VMs.min,
                                );
                              }
                            }}
                          />
                        </div>
                        <div className="input">
                          <NumericFormat
                            aria-labelledby="nbr_VMs"
                            required
                            isAllowed={withValueCapFiftyTh}
                            name="nbr_VMs"
                            type="text"
                            value={inputType?.nbr_VMs}
                            thousandSeparator={true}
                            decimalScale={0}
                            onValueChange={(e) => {
                              setInputType({
                                ...inputType,
                                nbr_VMs: e.floatValue ?? 0,
                              });
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {!isAdvanceSelected && (
                  <div className="slider-boxes">
                    <div className="shared-row" id="question-bg-grey">
                      <div className="slider-box">
                        <div className="question-row">
                          <div className="question-box first-box">
                            <p>
                              <FormattedMessage
                                {...messages.number_of_Vms_tshirt}
                              />
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="slider-box">
                        <div className="question-row">
                          <div className="question-box first-box">
                            <p>
                              <FormattedMessage {...messages.x_small} /> (
                              <strong className="number">1</strong> vCPU{' '}
                              <strong className="number">1GiB</strong> Memory)
                            </p>
                          </div>
                        </div>
                        <div className="question-row">
                          <div className="range-slider ranger-slider-top with-four-labels">
                            <div className="first-slide">
                              <Slider
                                id="xSmallSize"
                                min={0}
                                max={50000}
                                hideTextInput={true}
                                value={inputType?.xSmallSize}
                                onChange={({ value }: any) => {
                                  if (typeof value === 'number') {
                                    handleInputChange(value ?? 0, 'xSmallSize');
                                  }
                                }}
                              />
                            </div>
                            <div className="input">
                              <NumericFormat
                                aria-labelledby="xSmallSize"
                                required
                                className="number-input"
                                isAllowed={withValueCapFiftyTh}
                                name="xSmallSize"
                                type="text"
                                value={inputType?.xSmallSize.toLocaleString()}
                                thousandSeparator={true}
                                decimalScale={0}
                                onValueChange={(e) =>
                                  handleInputChange(
                                    e.floatValue ?? 0,
                                    'xSmallSize',
                                  )
                                }
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="slider-box">
                        <div className="question-row">
                          <div className="question-box">
                            <p>
                              <FormattedMessage {...messages.small} /> (
                              <strong className="number">1</strong> vCPU{' '}
                              <strong className="number">4GiB</strong> Memory)
                            </p>
                          </div>
                        </div>
                        <div className="question-row">
                          <div className="range-slider ranger-slider-top with-four-labels">
                            <div className="first-slide">
                              <Slider
                                id="xSmallSize"
                                min={0}
                                max={50000}
                                hideTextInput={true}
                                value={inputType?.smallSize}
                                onChange={({ value }: any) => {
                                  if (typeof value === 'number') {
                                    handleInputChange(value ?? 0, 'smallSize');
                                  }
                                }}
                              />
                            </div>
                            <div className="input">
                              <NumericFormat
                                aria-labelledby="smallSize"
                                required
                                isAllowed={withValueCapFiftyTh}
                                name="smallSize"
                                type="text"
                                value={inputType?.smallSize.toLocaleString()}
                                thousandSeparator={true}
                                decimalScale={0}
                                onValueChange={(e) =>
                                  handleInputChange(
                                    e.floatValue ?? 0,
                                    'smallSize',
                                  )
                                }
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="slider-box">
                        <div className="question-row">
                          <div className="question-box">
                            <p>
                              <FormattedMessage {...messages.medium} /> (
                              <strong className="number">2</strong> vCPU{' '}
                              <strong className="number">8GiB</strong> Memory)
                            </p>
                          </div>
                        </div>
                        <div className="question-row">
                          <div className="range-slider ranger-slider-top with-four-labels">
                            <div className="first-slide">
                              <Slider
                                id="mediumSize"
                                min={0}
                                max={50000}
                                hideTextInput={true}
                                value={inputType?.mediumSize}
                                onChange={({ value }: any) => {
                                  if (typeof value === 'number') {
                                    handleInputChange(value ?? 0, 'mediumSize');
                                  }
                                }}
                              />
                            </div>
                            <div className="input">
                              <NumericFormat
                                aria-labelledby="mediumSize"
                                required
                                isAllowed={withValueCapFiftyTh}
                                name="mediumSize"
                                type="text"
                                value={inputType?.mediumSize}
                                thousandSeparator={true}
                                decimalScale={0}
                                onValueChange={(e) =>
                                  handleInputChange(
                                    e.floatValue ?? 0,
                                    'mediumSize',
                                  )
                                }
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="slider-box">
                        <div className="question-row">
                          <div className="question-box">
                            <p>
                              <FormattedMessage {...messages.large} /> (
                              <strong className="number">4</strong> vCPU{' '}
                              <strong className="number">24GiB</strong> Memory)
                            </p>
                          </div>
                        </div>
                        <div className="question-row">
                          <div className="range-slider ranger-slider-top with-four-labels">
                            <div className="first-slide">
                              <Slider
                                id="largeSize"
                                min={0}
                                max={50000}
                                hideTextInput={true}
                                value={inputType?.largeSize}
                                onChange={({ value }: any) => {
                                  if (typeof value === 'number') {
                                    handleInputChange(value ?? 0, 'largeSize');
                                  }
                                }}
                              />
                            </div>
                            <div className="input">
                              <NumericFormat
                                aria-labelledby="largeSize"
                                required
                                isAllowed={withValueCapFiftyTh}
                                name="largeSize"
                                type="text"
                                value={inputType?.largeSize}
                                thousandSeparator={true}
                                decimalScale={0}
                                onValueChange={(e) =>
                                  handleInputChange(
                                    e.floatValue ?? 0,
                                    'largeSize',
                                  )
                                }
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="slider-box">
                        <div className="question-row">
                          <div className="question-box">
                            <p>
                              <FormattedMessage {...messages.x_large} /> (
                              <strong className="number">8</strong> vCPU{' '}
                              <strong className="number">48GiB</strong> Memory)
                            </p>
                          </div>
                        </div>
                        <div className="question-row">
                          <div className="range-slider ranger-slider-top with-four-labels">
                            <div className="first-slide">
                              <Slider
                                id="xLargeSize"
                                min={0}
                                max={50000}
                                hideTextInput={true}
                                value={inputType?.xLargeSize}
                                onChange={({ value }: any) => {
                                  if (typeof value === 'number') {
                                    handleInputChange(value ?? 0, 'xLargeSize');
                                  }
                                }}
                              />
                            </div>
                            <div className="input">
                              <NumericFormat
                                aria-labelledby="xLargeSize"
                                required
                                isAllowed={withValueCapFiftyTh}
                                name="xLargeSize"
                                type="text"
                                value={inputType?.xLargeSize}
                                thousandSeparator={true}
                                decimalScale={0}
                                onValueChange={(e) =>
                                  handleInputChange(
                                    e.floatValue ?? 0,
                                    'xLargeSize',
                                  )
                                }
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="slider-box">
                        <div className="question-row">
                          <div className="range-slider ranger-slider-top with-four-label">
                            <div className="first-slide"></div>
                            <div className="input total-box">
                              <p className="total">
                                <span>
                                  <FormattedMessage {...messages.total_Vms} />
                                </span>
                                <span className="green">
                                  {`${ROUNDUP(
                                    inputType?.totalVMs,
                                  )?.toLocaleString()}`}
                                </span>
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="slider-box" id="accordion-last">
                      <div className="question-row">
                        <div className="question-box name-box">
                          <p>
                            <FormattedMessage {...messages.mix_of_windows} />
                          </p>
                          <div
                            className="input-info"
                            style={{ marginRight: '16px' }}
                          >
                            <Tooltip id="ttnbrAdmins">
                              <p>
                                <FormattedMessage
                                  {...messages.mix_of_windows_tooltip}
                                />
                              </p>
                            </Tooltip>
                          </div>
                        </div>
                      </div>
                      <div className="question-row">
                        <div className="range-slider ranger-slider-top with-four-labels">
                          <div className="first-slide">
                            <Slider
                              id="replatform"
                              min={0}
                              max={100}
                              hideTextInput={true}
                              value={inputType?.replatform}
                              labelText={<span></span>}
                              onChange={({ value }: any) => {
                                if (typeof value === 'number') {
                                  setInputType({
                                    ...inputType,
                                    replatform: value ?? 0,
                                  });
                                }
                              }}
                            />
                          </div>
                          <div className="input">
                            <NumericFormat
                              aria-labelledby="replatform"
                              required
                              isAllowed={withValueCapHundred}
                              suffix="%"
                              name="replatform"
                              type="text"
                              value={inputType?.replatform}
                              thousandSeparator={true}
                              decimalScale={0}
                              onValueChange={(e) => {
                                setInputType({
                                  ...inputType,
                                  replatform: e.floatValue ?? 0,
                                });
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                <div className="grey-box first">
                  <Accordion>
                    <AccordionItem title={'Additional Information'}>
                      <div className="slider-boxes" id="accordion-2-box">
                        <div className="question-row">
                          <div className="question-box">
                            <div id="numberOfadmin">
                              <p className="accordion-title">
                                <FormattedMessage
                                  {...messages.informationa_purposes}
                                />
                              </p>
                            </div>
                          </div>
                        </div>
                        <div
                          className="question-row after-accordion"
                          id="question-bg-grey"
                        >
                          <div className="question-box storage-box">
                            <p>
                              <FormattedMessage {...messages.type_of_storage} />
                            </p>
                            <div className="input-field">
                              <Select
                                id="country"
                                className="spacer"
                                labelText=""
                                defaultValue={inputType?.storageType}
                                onChange={(e) => {
                                  const value = e.target.value as StorageTypes;
                                  setInputType({
                                    ...inputType,
                                    storageType: value,
                                  });
                                }}
                              >
                                {Object.values(StorageTypes).map((type) => (
                                  <SelectItem
                                    key={type}
                                    value={type}
                                    text={type}
                                  />
                                ))}
                              </Select>
                              <div className="input-info">
                                <Tooltip id="ttstorageType">
                                  <p>
                                    <FormattedMessage
                                      {...messages.type_of_storage_tooltip}
                                    />
                                  </p>
                                </Tooltip>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div
                          className="additional-inputs"
                          id="question-bg-grey"
                        >
                          <div className="question-row additional-top">
                            <div className="question-box name-box">
                              <p>
                                <FormattedMessage
                                  {...messages.current_vm_Workload_tooltip}
                                />
                              </p>
                              <div className="input-info">
                                <Tooltip>
                                  <p>
                                    <FormattedMessage
                                      {...messages.current_vm_Workload_tooltip}
                                    />
                                  </p>
                                </Tooltip>
                              </div>
                            </div>
                          </div>
                          <div className="slider-boxes">
                            <div className="slider-box">
                              <div className="question-row">
                                <div className="question-box">
                                  <p>
                                    <FormattedMessage {...messages.databases} />
                                  </p>
                                </div>
                              </div>
                              <div className="question-row">
                                <div className="range-slider ranger-slider-top with-four-labels">
                                  <div className="first-slide">
                                    <Slider
                                      id="vmWorkloadDatabase"
                                      min={0}
                                      max={state.max}
                                      hideTextInput={true}
                                      value={inputType?.vmWorkloadDatabase}
                                      invalid={true}
                                      onChange={({ value }: any) => {
                                        if (typeof value === 'number') {
                                          handleVMWorkloadFunctions(
                                            value,
                                            'vmWorkloadDatabase',
                                          );
                                        }
                                      }}
                                    />
                                  </div>
                                  <div className="input">
                                    <NumericFormat
                                      aria-labelledby="vmWorkloadDatabase"
                                      required
                                      isAllowed={withValueCapForVMWorkLoad}
                                      name="vmWorkloadDatabase"
                                      type="text"
                                      suffix={'%'}
                                      value={inputType?.vmWorkloadDatabase}
                                      thousandSeparator={true}
                                      decimalScale={0}
                                      onValueChange={(e) => {
                                        handleVMWorkloadFunctions(
                                          e.floatValue ?? 0,
                                          'vmWorkloadDatabase',
                                        );
                                      }}
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div className="slider-box">
                              <div className="question-row">
                                <div className="question-box">
                                  <p>
                                    <FormattedMessage {...messages.al_ml} />
                                  </p>
                                </div>
                              </div>
                              <div className="question-row">
                                <div className="range-slider ranger-slider-top with-four-labels">
                                  <div className="first-slide">
                                    <Slider
                                      id="vmWorkloadAIML"
                                      min={0}
                                      max={state.max}
                                      hideTextInput={true}
                                      value={inputType?.vmWorkloadAIML}
                                      onChange={({ value }: any) => {
                                        if (typeof value === 'number') {
                                          handleVMWorkloadFunctions(
                                            value,
                                            'vmWorkloadAIML',
                                          );
                                        }
                                      }}
                                    />
                                  </div>
                                  <div className="input">
                                    <NumericFormat
                                      aria-labelledby="vmWorkloadAIML"
                                      required
                                      isAllowed={withValueCapForVMWorkLoad}
                                      name="vmWorkloadAIML"
                                      type="text"
                                      suffix={'%'}
                                      value={inputType?.vmWorkloadAIML}
                                      thousandSeparator={true}
                                      decimalScale={0}
                                      onValueChange={(e) => {
                                        handleVMWorkloadFunctions(
                                          e.floatValue ?? 0,
                                          'vmWorkloadAIML',
                                        );
                                      }}
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div className="slider-box">
                              <div className="question-row">
                                <div className="question-box">
                                  <p>
                                    <FormattedMessage
                                      {...messages.data_analysis}
                                    />
                                  </p>
                                </div>
                              </div>
                              <div className="question-row">
                                <div className="range-slider ranger-slider-top with-four-labels">
                                  <div className="first-slide">
                                    <Slider
                                      id="vmWorkloadDataAnalytics"
                                      min={0}
                                      max={state.max}
                                      hideTextInput={true}
                                      value={inputType?.vmWorkloadDataAnalytics}
                                      onChange={({ value }: any) => {
                                        if (typeof value === 'number') {
                                          handleVMWorkloadFunctions(
                                            value,
                                            'vmWorkloadDataAnalytics',
                                          );
                                        }
                                      }}
                                    />
                                  </div>
                                  <div className="input">
                                    <NumericFormat
                                      aria-labelledby="vmWorkloadDataAnalytics"
                                      required
                                      isAllowed={withValueCapForVMWorkLoad}
                                      name="vmWorkloadDataAnalytics"
                                      type="text"
                                      suffix={'%'}
                                      value={inputType?.vmWorkloadDataAnalytics}
                                      thousandSeparator={true}
                                      decimalScale={0}
                                      onValueChange={(e) => {
                                        handleVMWorkloadFunctions(
                                          e.floatValue ?? 0,
                                          'vmWorkloadDataAnalytics',
                                        );
                                      }}
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div className="slider-box">
                              <div className="question-row">
                                <div className="question-box">
                                  <p>
                                    <FormattedMessage {...messages.messaging} />
                                  </p>
                                </div>
                              </div>
                              <div className="question-row">
                                <div className="range-slider ranger-slider-top with-four-labels">
                                  <div className="first-slide">
                                    <Slider
                                      id="vmWorkloadMessaging"
                                      min={0}
                                      max={state.max}
                                      hideTextInput={true}
                                      value={inputType?.vmWorkloadMessaging}
                                      onChange={({ value }: any) => {
                                        if (typeof value === 'number') {
                                          handleVMWorkloadFunctions(
                                            value,
                                            'vmWorkloadMessaging',
                                          );
                                        }
                                      }}
                                    />
                                  </div>
                                  <div className="input">
                                    <NumericFormat
                                      aria-labelledby="vmWorkloadMessaging"
                                      required
                                      isAllowed={withValueCapForVMWorkLoad}
                                      name="vmWorkloadMessaging"
                                      type="text"
                                      suffix={'%'}
                                      value={inputType?.vmWorkloadMessaging}
                                      thousandSeparator={true}
                                      decimalScale={0}
                                      onValueChange={(e) => {
                                        handleVMWorkloadFunctions(
                                          e.floatValue ?? 0,
                                          'vmWorkloadMessaging',
                                        );
                                      }}
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div className="slider-box">
                              <div className="question-row">
                                <div className="question-box">
                                  <p>
                                    <FormattedMessage
                                      {...messages.web_server}
                                    />
                                  </p>
                                </div>
                              </div>
                              <div className="question-row">
                                <div className="range-slider ranger-slider-top with-four-labels">
                                  <div className="first-slide">
                                    <Slider
                                      id="vmWorkloadWebServers"
                                      min={0}
                                      max={state.max}
                                      hideTextInput={true}
                                      value={inputType?.vmWorkloadWebServers}
                                      onChange={({ value }: any) => {
                                        if (typeof value === 'number') {
                                          handleVMWorkloadFunctions(
                                            value,
                                            'vmWorkloadWebServers',
                                          );
                                        }
                                      }}
                                    />
                                  </div>
                                  <div className="input">
                                    <NumericFormat
                                      aria-labelledby="vmWorkloadWebServers"
                                      required
                                      isAllowed={withValueCapForVMWorkLoad}
                                      name="vmWorkloadWebServers"
                                      type="text"
                                      suffix={'%'}
                                      value={inputType?.vmWorkloadWebServers}
                                      thousandSeparator={true}
                                      decimalScale={0}
                                      onValueChange={(e) => {
                                        handleVMWorkloadFunctions(
                                          e.floatValue ?? 0,
                                          'vmWorkloadWebServers',
                                        );
                                      }}
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="slider-box">
                            <div className="question-row">
                              <div className="range-slider ranger-slider-top with-four-label">
                                <div className="input total-box">
                                  <p className="total">
                                    <span>{'Total: '}</span>
                                    <span className="green">
                                      {totalWorkloadPercentage}%
                                    </span>
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="grey-box second ibm-flash">
                          <div className="question-row">
                            <div className="question-box name-box">
                              <p>
                                <FormattedMessage
                                  {...messages.percentage_of_your_workload}
                                />
                              </p>
                              <div className="input-info">
                                <Tooltip>
                                  <p>
                                    <FormattedMessage
                                      {...messages.percentage_of_your_workload_tooltip}
                                    />
                                  </p>
                                </Tooltip>
                              </div>
                            </div>
                          </div>
                          <div className="slider-boxes">
                            <div className="slider-box">
                              <div className="question-row">
                                <div className="question-box">
                                  <p>
                                    <FormattedMessage {...messages.databases} />
                                  </p>
                                </div>
                              </div>
                              <div className="question-row">
                                <div className="range-slider ranger-slider-top with-four-labels">
                                  <div className="first-slide">
                                    <Slider
                                      id="containerizeDatabases"
                                      min={0}
                                      max={100}
                                      hideTextInput={true}
                                      value={inputType?.containerizeDatabases}
                                      onChange={({ value }: any) => {
                                        if (typeof value === 'number') {
                                          setInputType({
                                            ...inputType,
                                            containerizeDatabases: value ?? 0,
                                          });
                                        }
                                      }}
                                    />
                                  </div>
                                  <div className="input">
                                    <NumericFormat
                                      aria-labelledby="containerizeDatabases"
                                      required
                                      isAllowed={withValueCapHundred}
                                      name="containerizeDatabases"
                                      type="text"
                                      suffix={'%'}
                                      value={inputType?.containerizeDatabases}
                                      thousandSeparator={true}
                                      decimalScale={0}
                                      onValueChange={(e) => {
                                        setInputType({
                                          ...inputType,
                                          containerizeDatabases:
                                            e.floatValue ?? 0,
                                        });
                                      }}
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div className="slider-box">
                              <div className="question-row">
                                <div className="question-box">
                                  <p>
                                    <FormattedMessage {...messages.al_ml} />
                                  </p>
                                </div>
                              </div>
                              <div className="question-row">
                                <div className="range-slider ranger-slider-top with-four-labels">
                                  <div className="first-slide">
                                    <Slider
                                      id="containerizeAIML"
                                      min={0}
                                      max={100}
                                      hideTextInput={true}
                                      value={inputType?.containerizeAIML}
                                      onChange={({ value }: any) => {
                                        if (typeof value === 'number') {
                                          setInputType({
                                            ...inputType,
                                            containerizeAIML: value ?? 0,
                                          });
                                        }
                                      }}
                                    />
                                  </div>
                                  <div className="input">
                                    <NumericFormat
                                      aria-labelledby="containerizeAIML"
                                      required
                                      isAllowed={withValueCapHundred}
                                      name="containerizeAIML"
                                      type="text"
                                      suffix={'%'}
                                      value={inputType?.containerizeAIML}
                                      thousandSeparator={true}
                                      decimalScale={0}
                                      onValueChange={(e) => {
                                        setInputType({
                                          ...inputType,
                                          containerizeAIML: e.floatValue ?? 0,
                                        });
                                      }}
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div className="slider-box">
                              <div className="question-row">
                                <div className="question-box">
                                  <p>
                                    <FormattedMessage
                                      {...messages.data_analysis}
                                    />
                                  </p>
                                </div>
                              </div>
                              <div className="question-row">
                                <div className="range-slider ranger-slider-top with-four-labels">
                                  <div className="first-slide">
                                    <Slider
                                      id="containerizeDataAnalytics"
                                      min={0}
                                      max={100}
                                      hideTextInput={true}
                                      value={
                                        inputType?.containerizeDataAnalytics
                                      }
                                      onChange={({ value }: any) => {
                                        if (typeof value === 'number') {
                                          setInputType({
                                            ...inputType,
                                            containerizeDataAnalytics:
                                              value ?? 0,
                                          });
                                        }
                                      }}
                                    />
                                  </div>
                                  <div className="input">
                                    <NumericFormat
                                      aria-labelledby="containerizeDataAnalytics"
                                      required
                                      isAllowed={withValueCapHundred}
                                      name="containerizeDataAnalytics"
                                      type="text"
                                      suffix={'%'}
                                      value={
                                        inputType?.containerizeDataAnalytics
                                      }
                                      thousandSeparator={true}
                                      decimalScale={0}
                                      onValueChange={(e) => {
                                        setInputType({
                                          ...inputType,
                                          containerizeDataAnalytics:
                                            e.floatValue ?? 0,
                                        });
                                      }}
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div className="slider-box">
                              <div className="question-row">
                                <div className="question-box">
                                  <p>
                                    <FormattedMessage {...messages.messaging} />
                                  </p>
                                </div>
                              </div>
                              <div className="question-row">
                                <div className="range-slider ranger-slider-top with-four-labels">
                                  <div className="first-slide">
                                    <Slider
                                      id="containerizeMessaging"
                                      min={0}
                                      max={100}
                                      hideTextInput={true}
                                      value={inputType?.containerizeMessaging}
                                      onChange={({ value }: any) => {
                                        if (typeof value === 'number') {
                                          setInputType({
                                            ...inputType,
                                            containerizeMessaging: value ?? 0,
                                          });
                                        }
                                      }}
                                    />
                                  </div>
                                  <div className="input">
                                    <NumericFormat
                                      aria-labelledby="containerizeMessaging"
                                      required
                                      isAllowed={withValueCapHundred}
                                      name="containerizeMessaging"
                                      type="text"
                                      suffix={'%'}
                                      value={inputType?.containerizeMessaging}
                                      thousandSeparator={true}
                                      decimalScale={0}
                                      onValueChange={(e) => {
                                        setInputType({
                                          ...inputType,
                                          containerizeMessaging:
                                            e.floatValue ?? 0,
                                        });
                                      }}
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div className="slider-box">
                              <div className="question-row">
                                <div className="question-box">
                                  <p>
                                    <FormattedMessage
                                      {...messages.web_server}
                                    />
                                  </p>
                                </div>
                              </div>
                              <div className="question-row">
                                <div className="range-slider ranger-slider-top with-four-labels">
                                  <div className="first-slide">
                                    <Slider
                                      id="containerizeWebServers"
                                      min={0}
                                      max={100}
                                      hideTextInput={true}
                                      value={inputType?.containerizeWebServers}
                                      onChange={({ value }: any) => {
                                        if (typeof value === 'number') {
                                          setInputType({
                                            ...inputType,
                                            containerizeWebServers: value ?? 0,
                                          });
                                        }
                                      }}
                                    />
                                  </div>
                                  <div className="input">
                                    <NumericFormat
                                      aria-labelledby="containerizeWebServers"
                                      required
                                      isAllowed={withValueCapHundred}
                                      name="containerizeWebServers"
                                      type="text"
                                      suffix={'%'}
                                      value={inputType?.containerizeWebServers}
                                      thousandSeparator={true}
                                      decimalScale={0}
                                      onValueChange={(e) => {
                                        setInputType({
                                          ...inputType,
                                          containerizeWebServers:
                                            e.floatValue ?? 0,
                                        });
                                      }}
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="check-question">
                          <div className="question-row">
                            <div className="question-box name-box">
                              <p style={{ width: '100%' }}>
                                <FormattedMessage
                                  {...messages.application_availablity}
                                />
                              </p>
                              <span className="Inline-circle">
                                <Tooltip>
                                  <p>
                                    <FormattedMessage
                                      {...messages.application_availablity_tooltip}
                                    />
                                  </p>
                                </Tooltip>
                              </span>
                            </div>
                          </div>
                          <div className="question-row">
                            <div className="input-field">
                              <div className="check-row">
                                <Checkbox
                                  labelText={
                                    <FormattedMessage {...messages.Snapshots} />
                                  }
                                  id="Snapshots"
                                  checked={inputType.snapshots}
                                  onChange={() => {
                                    setInputType({
                                      ...inputType,
                                      snapshots: !inputType.snapshots,
                                    });
                                  }}
                                />
                              </div>
                              <div className="check-row">
                                <Checkbox
                                  labelText={
                                    <FormattedMessage
                                      {...messages.crash_consistent}
                                    />
                                  }
                                  id="crashConsistent"
                                  checked={inputType.crashConsistent}
                                  onChange={() => {
                                    setInputType({
                                      ...inputType,
                                      crashConsistent:
                                        !inputType.crashConsistent,
                                    });
                                  }}
                                />
                              </div>
                              <div className="check-row">
                                <Checkbox
                                  labelText={
                                    <FormattedMessage
                                      {...messages.application_consistent}
                                    />
                                  }
                                  id="applicationConsistent"
                                  checked={inputType.applicationConsistent}
                                  onChange={() => {
                                    setInputType({
                                      ...inputType,
                                      applicationConsistent:
                                        !inputType.applicationConsistent,
                                    });
                                  }}
                                />
                              </div>
                              <div className="check-row">
                                <Checkbox
                                  labelText={
                                    <FormattedMessage
                                      {...messages.metro_disaster_recovery}
                                    />
                                  }
                                  id="metroDisaster"
                                  checked={inputType.metroDisaster}
                                  onChange={() => {
                                    setInputType({
                                      ...inputType,
                                      metroDisaster: !inputType.metroDisaster,
                                    });
                                  }}
                                />
                              </div>
                              <div className="check-row">
                                <Checkbox
                                  labelText={
                                    <FormattedMessage
                                      {...messages.regional_disaster_recovery}
                                    />
                                  }
                                  id="regionalDisaster"
                                  checked={inputType.regionalDisaster}
                                  onChange={() => {
                                    setInputType({
                                      ...inputType,
                                      regionalDisaster:
                                        !inputType.regionalDisaster,
                                    });
                                  }}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </AccordionItem>
                  </Accordion>
                </div>
              </div>
            </div>
            {/* {page 2!!!!!!!!!!} */}
          </div>
          <div className="result-section">
            <div className="result-label"></div>
            <div className={`result-section `}>
              <div className="result-label">
                <p className="details">
                  <span className="blue">
                    <NumericFormat
                      required
                      displayType="text"
                      value={formatNumberWithAbbreviations(
                        state.fusionHCIData.totalCost.totalSavings,
                      )}
                      prefix={DisplaySymbol(inputType?.currency)}
                      thousandSeparator={true}
                    />
                    {formatNumberWithAbbreviationsLetters(
                      state.fusionHCIData.totalCost.totalSavings,
                    )}
                  </span>{' '}
                  {intl.formatMessage(messages.total_savings)}
                </p>
              </div>
              <div
                className={`graph-container ${
                  inputType.openResult ? 'open' : 'open'
                }`}
              >
                <TCOBarChart
                  totalCurrentCost={state.fusionHCIData.totalCurrentCost}
                  totalIBMCost={state.fusionHCIData.totalIBMCost}
                  inputType={inputType}
                  intl={intl}
                  onChartReady={setChartDataUrl}
                />
              </div>
            </div>
            <div className="buttons">
              {inputType.openResult && (
                <PDFDownloadLink
                  document={pdfObject}
                  fileName={'FusionReport'}
                  className="home"
                >
                  {() => (
                    <Button className="home">
                      {intl.formatMessage(messages.view_your_report)}
                    </Button>
                  )}
                </PDFDownloadLink>
              )}
              {!inputType.openResult && (
                <Button
                  onClick={() => {
                    initUrxForm(
                      environment,
                      URX_FORM.INSTANCE_ID,
                      URX_FORM.FORM_ID,
                      URX_FORM.LOCALE as IBMLocale,
                      URX_FORM.COMPANY_ID,
                    );
                    setDrawerOpen(true);
                    setCookie();
                  }}
                  className="home"
                >
                  {intl.formatMessage(messages.see_your_result)}
                </Button>
              )}
              <Button
                kind="tertiary"
                onClick={() => setShowAssumption(true)}
                className="dspm__bluehighlight"
              >
                {intl.formatMessage(messages.view_modify_assumption)}
              </Button>
              {inputType.openResult && (
                <a
                  className="a-info-title home"
                  href="https://www.ibm.com/products/storage-fusion?utm_content=SRCWW&p1=Search&p4=43700076382506025&p5=e&p9=58700007039170147&gclid=Cj0KCQjw4MSzBhC8ARIsAPFOuyU1AA_29Lu7ffgM-dvkIkDhkgB89a123u6VR6196-B5gOaXi8ypZq0aAq0FEALw_wcB&gclsrc=aw.ds"
                  target="_blank"
                  rel="noreferrer"
                >
                  <FormattedMessage {...messages.more_info} />
                </a>
              )}
            </div>
          </div>
        </div>
      </div>
      <BlobProvider document={pdfObject}>
        {({ blob, url, loading, error }) => (
          <PDFDownloadLink
            document={pdfObject}
            fileName={'FusionReport.pdf'}
            style={{ display: 'none' }}
          >
            {({ blob, url, loading, error }) => {
              if (triggerDownload && url) {
                const link = document.createElement('a');
                link.href = url;
                link.download = 'FusionReport.pdf';
                link.click();
                setTriggerDownload(false);
              }
              return null;
            }}
          </PDFDownloadLink>
        )}
      </BlobProvider>
    </div>
  );
};

export default ESGIBMFusion;
